import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Diamond, ChevronRight } from "lucide-react";

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

export default function FeaturedSpotlight({ companions }) {
  const [active, setActive] = useState(0);
  const items = (companions || []).slice(0, 5);

  useEffect(() => {
    if (items.length <= 1) return;
    const id = setInterval(() => {
      setActive((a) => (a + 1) % items.length);
    }, 5500);
    return () => clearInterval(id);
  }, [items.length]);

  if (items.length === 0) return null;
  const c = items[active];

  return (
    <section className="relative overflow-hidden" data-testid="featured-spotlight">
      <div className="relative h-[80vh] min-h-[600px]">
        {items.map((it, i) => (
          <div
            key={it.id}
            className="absolute inset-0 transition-opacity duration-[1400ms] ease-in-out"
            style={{
              opacity: i === active ? 1 : 0,
              backgroundImage: `linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.3) 100%), url('${resolveUrl(it.cover_photo || it.photos?.[0])}')`,
              backgroundSize: "cover",
              backgroundPosition: "center 25%",
              transform: i === active ? "scale(1)" : "scale(1.04)",
              transitionProperty: "opacity, transform",
              transitionDuration: "1400ms, 8000ms",
            }}
          />
        ))}
        <div className="grain absolute inset-0 pointer-events-none" />

        <div className="relative h-full max-w-[1400px] mx-auto px-6 md:px-12 flex items-end pb-20">
          <div key={c.id} className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4 fade-up">
              <Diamond size={12} className="text-[var(--gold)] gold-pulse" />
              <div className="label-luxury text-[var(--gold)]">Tonight's Spotlight</div>
            </div>
            <h2 className="font-serif-display text-[clamp(3rem,8vw,7rem)] leading-[0.95] font-light tracking-tight fade-up delay-200">
              <span className="gold-text italic">{c.name}</span>
            </h2>
            <div className="text-white/70 text-sm tracking-[0.3em] uppercase mt-6 fade-up delay-300">
              {c.age} years · {c.nationality} · {c.location}
            </div>
            <p className="text-white/65 mt-6 max-w-lg leading-relaxed fade-up delay-500">
              {c.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10 fade-up delay-700">
              <Link
                to={`/companions/${c.id}`}
                className="btn-gold btn-gold-solid"
                data-testid={`spotlight-view-${c.id}`}
              >
                View Profile <ChevronRight size={14} />
              </Link>
              <Link to="/contact" className="btn-gold">
                Arrange an Evening
              </Link>
            </div>
          </div>
        </div>

        {/* Dots */}
        {items.length > 1 && (
          <div className="absolute bottom-8 right-12 hidden md:flex gap-2 z-10">
            {items.map((it, i) => (
              <button
                key={it.id}
                onClick={() => setActive(i)}
                aria-label={`Slide ${i + 1}`}
                data-testid={`spotlight-dot-${i}`}
                className={`h-1 transition-all duration-500 ${
                  i === active ? "w-12 bg-[var(--gold)]" : "w-6 bg-white/30 hover:bg-white/60"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
